import { useQuery } from 'react-query'
import { NumericFormat } from 'react-number-format'
import { getAdminDashboardStats } from 'services/fund'
import Num2persian from 'utilities/helper/num2Persian'
import { faFull, faPlain, toman } from 'utilities/helper/format'

const MIN_AMOUNT = 5_000

function CapacityRow({
    label,
    value,
    tone,
}: {
    label: string
    value: string
    tone?: 'strong' | 'muted'
}) {
    return (
        <div className=" flex items-center justify-between gap-2 border-b border-divider py-1.5 last:border-b-0">
            <span className=" text-[11px] text-text-secondary">{label}</span>
            <span
                className={` whitespace-nowrap text-[11px] tabular-nums ${
                    tone === 'strong'
                        ? ' text-sm font-bold text-brand-deep'
                        : tone === 'muted'
                        ? ' text-text-secondary'
                        : ' font-bold text-text-body'
                }`}
            >
                {value}
            </span>
        </div>
    )
}

/**
 * Step 2 — «چقدر؟».
 *
 * The old form showed «موجودی صندوق» as one number. That number is not what
 * a manager can lend: part of it is mid-settlement and unspendable, and part
 * of it sits in the manager's own account, which is spendable but only after
 * they move it themselves. So the real capacity is broken out, and the amount
 * is checked against it — as a warning, never a block. Funds do lend more
 * than they hold on paper (money arrives the same week), and a screen that
 * refuses is a screen the manager works around.
 */
export default function StepAmount({
    moneypoolId,
    amount,
    onChange,
    requestedAmount,
}: {
    moneypoolId?: string
    amount: string
    onChange: (next: string) => void
    /** what the member asked for, when they filed a request */
    requestedAmount?: number
}) {
    const { data } = useQuery(
        ['admin-dashboard-stats', moneypoolId],
        getAdminDashboardStats,
        { enabled: !!moneypoolId, staleTime: 60_000 }
    )

    const capacity = data?.capacity
    const financial = data?.financial
    const payable =
        capacity?.payable ??
        (financial
            ? financial.hamyan_balance + financial.bank_balance
            : undefined)

    const numericAmount = Number(amount || 0)
    const overCapacity =
        payable !== undefined && numericAmount > payable && numericAmount > 0

    return (
        <>
            <div className=" rounded-2xl border border-stroke bg-white p-4">
                <label
                    htmlFor="loan-amount"
                    className=" text-xs text-text-secondary"
                >
                    مبلغ وام (تومان)
                </label>
                <NumericFormat
                    id="loan-amount"
                    thousandSeparator
                    allowNegative={false}
                    inputMode="numeric"
                    value={amount}
                    onValueChange={(values) => onChange(values.value)}
                    placeholder="۰"
                    className=" mt-1 w-full border-b border-stroke bg-transparent py-2 text-lg font-bold tabular-nums text-heading outline-none placeholder:text-text-muted"
                />
                {amount !== '' && numericAmount > 0 && (
                    <p className=" mt-2 text-[11px] text-blue-1">
                        {Num2persian(String(amount))} تومان
                    </p>
                )}
                {amount !== '' && numericAmount < MIN_AMOUNT && (
                    <p className=" mt-2 text-[11px] text-danger-on">
                        حداقل مبلغ وام {faFull.format(MIN_AMOUNT)} تومان است.
                    </p>
                )}

                {/* only worth offering once the manager has changed it away
                    from the request — otherwise it is a button that does
                    nothing, next to the value it would set */}
                {!!requestedAmount && numericAmount !== requestedAmount && (
                    <button
                        type="button"
                        onClick={() => onChange(String(requestedAmount))}
                        className=" mt-3 w-full rounded-full border border-brand bg-brand-soft py-2 text-[11px] font-bold text-brand-deep"
                    >
                        بازگشت به مبلغ درخواستی: {toman(requestedAmount)}
                    </button>
                )}
            </div>

            <div className=" mt-3 rounded-2xl border border-stroke bg-white p-4">
                <p className=" mb-1 text-xs font-bold text-brand-deep">
                    توان صندوق
                </p>
                {payable === undefined ? (
                    <p className=" text-[11px] text-text-secondary">
                        در حال خواندن موجودی…
                    </p>
                ) : (
                    <>
                        <CapacityRow
                            label="قابل پرداخت همین حالا"
                            value={toman(payable)}
                            tone="strong"
                        />
                        {capacity && (
                            <>
                                <CapacityRow
                                    label="نزد همیان"
                                    value={toman(capacity.at_hamyan)}
                                />
                                <CapacityRow
                                    label="نزد مدیر"
                                    value={toman(capacity.at_manager)}
                                />
                                {capacity.inprogress > 0 && (
                                    <CapacityRow
                                        label="در حال انتقال (فعلاً قابل استفاده نیست)"
                                        value={toman(capacity.inprogress)}
                                        tone="muted"
                                    />
                                )}
                                {capacity.exit_commitment_amount > 0 && (
                                    <CapacityRow
                                        label={`تعهد به ${faPlain.format(
                                            capacity.exit_commitment_members_count
                                        )} درخواست تسویه`}
                                        value={toman(
                                            capacity.exit_commitment_amount
                                        )}
                                        tone="muted"
                                    />
                                )}
                            </>
                        )}
                    </>
                )}

                {overCapacity && (
                    <p className=" mt-2 rounded-lg bg-warning-soft px-2 py-1.5 text-[11px] leading-5 text-warning-on">
                        این مبلغ از توان فعلی صندوق بیشتر است. اگر مطمئنید پول
                        تا زمان پرداخت می‌رسد، می‌توانید ادامه دهید.
                    </p>
                )}
            </div>
        </>
    )
}
